import classnames from 'classnames/bind'
import fit from 'math-fit'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { GlobalThemeColors } from '~/@types/colors'

import {
  useIsomorphicLayoutEffect,
  useSpring,
  useWindowSize,
} from '@unlikelystudio/react-hooks'

import Video from '~/components/Abstracts/Video'
import Image from '~/components/UI/Image'

import useBodyLockScroll from '~/hooks/useBodyScrollLock'
import useOnKeyDown from '~/hooks/useOnKeyDown'

import BlurredCta from '../BlurredCta'
import { ArrowIcon, CloseIcon } from '../Icons'
import { ProductHeaderMediaProps } from '../ProductHeaderMedia'
import css from './styles.module.scss'

const cx = classnames.bind(css)

const SPRING_CONFIG = {
  set: {
    interpolation: 'basic',
    friction: 1,
    precision: 0.1,
    perfectStop: true,
  },
  image: {
    interpolation: 'basic',
    friction: 10,
    precision: 0.1,
    perfectStop: true,
  },
  cursor: {
    interpolation: 'basic',
    friction: 4.5,
    precision: 0.1,
    perfectStop: true,
  },
}

export interface FullScreenMediaProps {
  images: ProductHeaderMediaProps['items']
  className?: string
  onExitFullscreen?: () => void
  onPreviousClick?: () => void
  onNextClick?: () => void
}

function getValidValue(val: number) {
  if (val < 0) return 0
  if (val > 1) return 100
  return val * 100
}

function FullScreenMedia({
  className,
  images,
  onPreviousClick,
  onNextClick,
  onExitFullscreen,
}: FullScreenMediaProps) {
  const fullScreenImageRef = useRef<HTMLDivElement>()
  const fullScreenImageContainerRef = useRef<HTMLDivElement>()
  const pointerCoords = useRef({ x: undefined, y: undefined })

  const windowSize = useWindowSize()
  useBodyLockScroll(true)

  const [fullScreenImageSize, setFullScreenMediaSize] = useState({
    width: 0,
    height: 0,
  })

  const [setFullScreenImgSpring] = useSpring({
    x: 0,
    y: 0,
    config: SPRING_CONFIG.image,
    onUpdate: ({ x, y }) => {
      if (fullScreenImageContainerRef.current) {
        fullScreenImageContainerRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`
      }
    },
  })

  const updatePositions = (set = false) => {
    const { x, y } = pointerCoords.current
    if (x === undefined && y === undefined) return

    const w = fullScreenImageSize.width - windowSize.width
    const h = fullScreenImageSize.height - windowSize.height

    const xPos = getValidValue(x / windowSize.width)
    const yPos = getValidValue(y / windowSize.height)

    setFullScreenImgSpring({
      config: set ? SPRING_CONFIG.set : SPRING_CONFIG.image,
      x: -((w / 100) * Math.min(windowSize.width, xPos)),
      y: -((h / 100) * Math.min(windowSize.height, yPos)),
    })
  }

  useEffect(() => {
    updatePositions(true)
  }, [fullScreenImageSize])

  const updatePointerPositions = useCallback(
    (x: number, y: number) => {
      pointerCoords.current = { x, y }
      updatePositions()
    },
    [fullScreenImageSize],
  )

  const onMouseMove = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      updatePointerPositions(e.clientX, e.clientY)
    },
    [updatePointerPositions],
  )

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      updatePointerPositions(
        windowSize.width - e.touches[0].clientX,
        windowSize.height - e.touches[0].clientY,
      )
    },
    [windowSize, updatePointerPositions],
  )

  useIsomorphicLayoutEffect(() => {
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('touchmove', onTouchMove)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [onMouseMove, onTouchMove])

  useIsomorphicLayoutEffect(() => {
    if (images?.[0]?.video && windowSize) {
      setFullScreenMediaSize({
        width: windowSize.width,
        height: windowSize.height,
      })
      updatePositions()
    } else if (images?.[0].image) {
      const { width, height } = fit(
        { width: images?.[0].image.width, height: images?.[0].image.height },
        windowSize,
      )

      if (width && height) {
        setFullScreenMediaSize({ width, height })
        updatePositions()
      }
    }
  }, [windowSize, images?.[0]?.image?.src, images?.[0]?.video])

  useOnKeyDown(27, onExitFullscreen)
  useOnKeyDown(37, onPreviousClick)
  useOnKeyDown(39, onNextClick)

  return (
    <div
      tabIndex={0}
      ref={fullScreenImageRef}
      onMouseEnter={onMouseMove}
      className={cx(className, css.FullScreenMedia)}>
      <div className={css.controls}>
        <div className={css.prevNextControls}>
          {onPreviousClick && (
            <BlurredCta
              theme={GlobalThemeColors.White}
              className={cx(css.cta)}
              onClick={onPreviousClick}>
              <ArrowIcon className={css.arrowIconPrev} />
            </BlurredCta>
          )}
          {onNextClick && (
            <BlurredCta
              theme={GlobalThemeColors.White}
              className={cx(css.cta)}
              onClick={onNextClick}>
              <ArrowIcon className={css.arrowIcon} />
            </BlurredCta>
          )}
        </div>
        <BlurredCta
          className={cx(css.cta, css.closeCta)}
          theme={GlobalThemeColors.White}
          onClick={onExitFullscreen}>
          <CloseIcon className={cx(css.closeIcon)} />
        </BlurredCta>
      </div>
      <div
        className={css.fullScreenImageContainer}
        ref={fullScreenImageContainerRef}
        onClick={onExitFullscreen}
        style={fullScreenImageSize}>
        {images?.map((image, index) => (
          <>
            {image?.video ? (
              <div className={css.video}>
                <Video
                  src={image?.video}
                  loop
                  loading="lazy"
                  autoPlay
                  playsInline
                  muted
                  objectFit="cover"
                />
              </div>
            ) : (
              <Image
                layout="fill"
                objectFit="cover"
                className={cx(css.image, { isAbsolute: index > 0 })}
                asPlaceholder
                {...image.image}
              />
            )}
          </>
        ))}
      </div>
    </div>
  )
}

FullScreenMedia.defaultProps = {}

export default FullScreenMedia
